import { inject as service } from '@ember/service';
import { alias, filterBy, readOnly, sort } from '@ember/object/computed';
import { computed } from '@ember/object';
import { isBlank, isEmpty, isNone } from '@ember/utils';
import { Promise } from 'rsvp';
import DS from 'ember-data';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { hasMany } from 'ember-data/relationships';
import DeliveryModel from 'cio/models/delivery';
import CustomerSegmentModel from 'cio/models/customer-segment';
import SubjectModel from 'cio/models/subject';
import SegmentModel from 'cio/models/segment';
import LogModel from 'cio/models/log';
import { CustomerIdentifiers, IdentifierKey, isAttributeEnabledIdentifier } from 'cio/utils/identifiers';
import { CIO_SUBSCRIPTION_PREF_KEY, RESTRICTED_ATTRIBUTES } from 'cio/utils/attributes';
import { SubscriptionPreferences } from 'cio/utils/subscription-center';
import EnvironmentService from 'cio/services/environment';
import DeviceModel from 'cio/models//device';
import ObjectModel from 'cio/models/object';
import RelationshipModel from 'cio/models/relationship';
import ApiService, { RequestOptions } from 'cio/services/api';
import { modelApiAction } from 'cio/utils/model-api-actions';
import SubscriptionCenterService from 'cio/services/subscription-center';

const { PromiseArray } = DS;

const FIRST_NAME_ATTRIBUTE_KEYS = [
  'first_name',
  'firstname', // eslint-disable-line spellcheck/spell-checker
  'first name',
];
const LAST_NAME_ATTRIBUTE_KEYS = [
  'last_name',
  'lastname', // eslint-disable-line spellcheck/spell-checker
  'last name',
  'surname',
];
const FULL_NAME_ATTRIBUTE_KEYS = [
  'full_name',
  'fullname', // eslint-disable-line spellcheck/spell-checker
  'full name',
  'name',
];

export default class CustomerModel extends Model {
  @service api!: ApiService;
  @service store!: DS.Store;
  @service environment!: EnvironmentService;
  @service subscriptionCenter!: SubscriptionCenterService;

  hiddenAttrs = [];

  @attr('json', { defaultValue: '{}' }) attributes!: string;
  @attr({ defaultValue: () => ({}) }) identifiers!: CustomerIdentifiers;
  @attr('number') lastVisited!: number;
  @attr('boolean') unsubscribed!: boolean;
  @attr('boolean') unsentDrafts!: boolean;
  @alias('unsubscribed') isGloballyUnsubscribed!: boolean;

  @hasMany('delivery', { async: true, inverse: 'customer' }) deliveries!: DS.PromiseManyArray<DeliveryModel>;
  @hasMany('customer-segment', { async: true, inverse: null }) segments!: DS.PromiseManyArray<CustomerSegmentModel>;
  @hasMany('subject', { async: true, inverse: 'customer' }) subjects!: DS.PromiseManyArray<SubjectModel>;
  @hasMany('subject', { async: true, inverse: null }) eventSubjects!: DS.PromiseManyArray<SubjectModel>;
  @hasMany('device', { async: true, inverse: 'customer' }) devices!: DS.PromiseManyArray<DeviceModel>;
  @hasMany('relationship', { async: true, inverse: 'customer' }) relationships!: DS.PromiseManyArray<RelationshipModel>;

  queryLogs(
    params: Partial<{
      type: string;
      name: string;
      internal_id: string;
      limit: number;
      filters: string;
    }>,
  ): DS.PromiseArray<LogModel, DS.AdapterPopulatedRecordArray<LogModel>> {
    let { isNew } = this;
    let query = {
      since: true,
      ...params,
    };

    // @ts-ignore isNew is not typed correctly
    if (isNew) {
      // @ts-ignore - DS.PromiseArray.create(...) is not typed correctly (but this code works)
      return PromiseArray.create({ promise: Promise.resolve([]) });
    }

    query.internal_id = this.identifiers[IdentifierKey.CioId];

    let promise = this.store.query('log', query);

    if (query.filters != null || query.limit != null) {
      let newPromise = new Promise((resolve, reject) => {
        promise
          .then((logs) => {
            let filteredLogs: LogModel[] = [];
            if (query.filters) {
              filteredLogs = logs.filter((l: LogModel) => (logs as any).meta.filter_matches.includes(l.id));
            }

            // FIXME: Remove this once the new ui_api is shipped because
            // it will have proper support for the limit param
            if (query.limit) {
              filteredLogs = logs.slice(0, query.limit);
            }
            resolve(filteredLogs);
          })
          .catch(reject);
      });

      // @ts-ignore - DS.PromiseArray.create(...) is not typed correctly (but this code works)
      return PromiseArray.create({ promise: newPromise });
    } else {
      return promise;
    }
  }

  @computed('attributes')
  get attrs(): Record<string, unknown> {
    return JSON.parse(this.attributes);
  }
  set attrs(value) {
    let json = JSON.stringify(value);
    this.set('attributes', json);
  }

  get attributeKeys(): string[] {
    return Object.keys(this.attrs);
  }

  @readOnly('attrs.email') email!: string;

  get subscriptionPreferences(): SubscriptionPreferences {
    if (isEmpty(<string>this.attrs.cio_subscription_preferences)) {
      return <SubscriptionPreferences>{};
    }

    try {
      return JSON.parse(<string>this.attrs.cio_subscription_preferences);
    } catch {
      return <SubscriptionPreferences>{};
    }
  }

  async updateSubscriptionPreferences(preferences: SubscriptionPreferences) {
    if (!this.subscriptionCenter.initialized) {
      await this.subscriptionCenter.initializeTask.perform();
    }

    let topicIds = this.subscriptionCenter.topics.map((topic) => topic.id);
    let topicEntries = Object.entries(preferences.topics).filter(([topic]) => {
      let id = topic.replace('topic_', '');

      return topicIds.includes(id);
    });

    this.attrs = {
      ...this.attrs,
      [CIO_SUBSCRIPTION_PREF_KEY]: JSON.stringify({
        ...preferences,
        topics: Object.fromEntries(topicEntries),
      }),
    };

    if (this.isGloballyUnsubscribed) {
      this.attrs = {
        ...this.attrs,
        unsubscribed: false,
      };
    }

    await this.save();
  }

  @computed('emailOrId', 'fullName')
  get displayName() {
    if (!isBlank(this.fullName)) {
      return this.fullName;
    }

    return this.emailOrId;
  }

  @computed('email', 'id')
  get emailOrId() {
    let { email } = this;

    if (!isBlank(email)) {
      return email;
    }

    return `Person with id ${this.identifiers.id}`;
  }

  private get enabledIdentifiers() {
    let { identifiers } = this;

    return Object.keys(identifiers).filter((identifier) =>
      isAttributeEnabledIdentifier(identifier, this.environment.current.identifiers),
    );
  }

  @readOnly('attrs.created_at') createdAt!: string;

  getFirstAttributeValueFromKeys(keys: string[]): string | null {
    for (let key of keys) {
      let attributeKey = this.attributeKeys.find((k) => k.toLowerCase() === key);
      let value = attributeKey != null ? this.attrs[attributeKey] : null;
      if (value != null) {
        return value as string;
      }
    }

    return null;
  }

  get firstName(): string | null {
    return this.getFirstAttributeValueFromKeys(FIRST_NAME_ATTRIBUTE_KEYS);
  }
  get lastName(): string | null {
    return this.getFirstAttributeValueFromKeys(LAST_NAME_ATTRIBUTE_KEYS);
  }
  get fullName(): string | null {
    let { firstName, lastName } = this;
    if (!isBlank(firstName) && !isBlank(lastName)) {
      return `${firstName} ${lastName}`;
    }

    let fullName = this.getFirstAttributeValueFromKeys(FULL_NAME_ATTRIBUTE_KEYS);
    if (!isBlank(fullName)) {
      return fullName;
    }

    return null;
  }

  temporaryMergeProfile = false;

  // For attribute lists, we want to make sure that there are three primary groupings displayed,
  // in order.
  // 1. Identifier attributes. These can grow over time, so we need to be generic about it
  // 2. Restricted/reserved attributes. These probably won't grow over time, but they may not all
  //    be present.
  // 3. The rest of the attributes, in alphabetical order.
  @computed('attrs')
  get filteredAttrsWithIdentifiers() {
    let { attrs } = this;

    // Remove identifiers, restricted and 'cio_subscription_preferences key' attributes and sort them alphabetically
    let customAttributeKeys = Object.keys(attrs)
      .reject(
        (key) =>
          this.enabledIdentifiers.includes(key) ||
          RESTRICTED_ATTRIBUTES.includes(key) ||
          key === CIO_SUBSCRIPTION_PREF_KEY,
      )
      .sort();

    // Create a list of attribute *keys* based on the order outlined above. They may not all have values
    let allAttributeKeys = [...new Set([...this.enabledIdentifiers, ...RESTRICTED_ATTRIBUTES, ...customAttributeKeys])];
    let attributes = allAttributeKeys
      .filter((key) => {
        if (isNone(attrs[key]) && !(key in this.identifiers)) {
          return false;
        }

        return true;
      })
      .map<Record<string, any>>((key) => {
        return {
          [key]: attrs[key] || '',
        };
      })
      .filter((attr) => {
        // Only filters out totally absent attributes. If an attribute is set to an empty string,
        // it's probably a new customer, so it shouldn't be filtered out.
        if (!attr) {
          return false;
        }

        let customerIdentifiers = Object.keys(this.identifiers);
        let [key] = Object.keys(attr);

        // If this is a new customer record, we don't need complicated filtering since it's a pure
        // record created by the route. We just don't want to show the cio_id at all.
        // However, when we perform a profile merge, we create a temporary
        // customer model to show the merge results. In this case, we DO want to
        // show cio_id.
        // @ts-ignore isNew is not typed correctly
        if (this.isNew && !this.temporaryMergeProfile) {
          return !isNone(attr[key]) && key !== IdentifierKey.CioId;
        }

        // Non-multi-id workspaces have cio_id as an identifier, but won't return it in the attributes.
        // Let's filter it out for those customers.
        if (key === IdentifierKey.CioId && !attr[key]) {
          return false;
        }

        // We want to show all available identifiers to customers, even if they're blank
        if (customerIdentifiers.includes(key)) {
          return true;
        }

        return !!attr[key];
      });

    // Keep new attribute at the bottom of the attributes list
    if (!isNone(attrs[''])) {
      attributes.push({
        '': attrs[''],
      });
    }

    return attributes;
  }

  @computed('filteredAttrsWithIdentifiers')
  get filteredAttrs() {
    return this.filteredAttrsWithIdentifiers.filter((attr) => {
      return Object.values(attr)[0];
    });
  }

  isCustomAttribute(key: string) {
    return !(this.enabledIdentifiers.includes(key) || RESTRICTED_ATTRIBUTES.includes(key));
  }

  @sort('devices.content', 'deviceSorting') sortedDevices!: DeviceModel[];
  deviceSorting = Object.freeze(['lastUsed:desc']);

  @filterBy('segments.content', 'type', 'static') staticSegments!: SegmentModel[];
  @filterBy('segments.content', 'type', 'dynamic') dynamicSegments!: SegmentModel[];

  saveAndQueue = modelApiAction({
    path: '',
    type: 'POST',
    collection: true,
    before() {
      return { customer: this.serialize() };
    },
  });

  getRenderedShortcuts = modelApiAction({
    path: 'profile_shortcuts',
    type: 'GET',
    after(response) {
      return response.profile_shortcuts;
    },
  });

  async getRelatedObjects(objectTypeId = '', query?: RequestOptions['data']) {
    let customerId = this.identifiers[IdentifierKey.CioId] || this.id;
    if (customerId != null) {
      let relationships = await this.store.query('relationship', {
        ...query,
        customerId,
        objectTypeId,
      });
      return {
        objects: relationships.map((item) => item.belongsTo('object').value() as ObjectModel),
        relationships: relationships.slice(),

        // "meta" not typed:
        meta: (relationships as any).meta,
      };
    }

    return {
      objects: [],
      relationships: [],
      meta: {
        pagination: { page: 1, size: query?.size || 10, total: -1 },
      },
    };
  }

  async getRelatedObjectsPerType() {
    return await this.getRelatedObjects('', { allObjectTypes: true });
  }

  // used to temporarily update sentAttributes after creating a new person with new attribute
  updateSentAttributes(attributes: Record<string, any>) {
    let attrs = [...(this.environment.sentAttributes ?? [])];
    for (let key in attributes) {
      if (!attrs.includes(key)) {
        attrs.push(key);
      }
    }

    this.environment.set('sentAttributes', attrs);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    customer: CustomerModel;
  }
}
